import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { es, enUS, Locale } from "date-fns/locale";
import { Calculator, FileSpreadsheet, Pencil } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { companyAtom } from "@/store/company";
import { PAGE_SIZE } from "@/store";
import { languageAtom } from "@/store/language";
import { Pagination } from "@/components/pagination";
import { RechargesForm } from "./RechargesForm";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/PageHeader";
import RechargesIcon from "@/components/icons/RechargesIcon";

// Define the type for emission items
interface EmissionListType {
  id: number;
  facility: string;
  fuel_type: string;
  quantity: number;
  unit?: string;
  invoice_amount: number;
  date: string;
  invoice?: string;
  co2_eq?: number;
}

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: EmissionListType[];
}

export function RechargesList() {
  const { t } = useTranslation();
  const [company] = useAtom(companyAtom);
  const [language] = useAtom(languageAtom);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedEmissionId, setSelectedEmissionId] = useState<number | null>(
    null,
  );
  const { token } = useAuth();

  // Map language code to date-fns locale
  const localeMap: Record<string, Locale> = {
    en: enUS,
    es: es,
  };

  // Use the appropriate locale based on the selected language
  const locale = localeMap[language || "en"];

  const { data, isLoading } = useQuery<ApiResponse>({
    queryKey: ["emission-facilities", company?.id, currentPage],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(
          `/emission-facilities/?page=${currentPage}&page_size=${PAGE_SIZE}&company=${company?.id}`,
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch company");
      }
      const res = await response.json();
      return res;
    },
    enabled: !!company?.id,
  });

  const handleEdit = (id: number) => {
    setSelectedEmissionId(id);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setSelectedEmissionId(null);
    setIsDrawerOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        icon={
          <RechargesIcon
            width={180}
            height={180}
            className="w-[50px] h-[50px]"
          />
        }
        actionTitle="recharges.add"
        title="recharges.title"
        description="recharges.description"
        handleNew={handleAdd}
      />

      <div className="flex items-center space-x-2 p-4">
        {/* <Input
                id="name-filter"
                placeholder={t("filters.filterByName")}
                onChange={(e) => debouncedSetName(e.target.value)}
                className="max-w-sm"
              />
              <Input
                id="email-filter"
                placeholder={t("filters.filterByEmail")}
                onChange={(e) => debouncedSetEmail(e.target.value)}
                className="max-w-sm"
              />
              <Button
                onClick={handleClearFilters}
                variant="default"
                className="bg-primary text-primary-foreground hover:bg-primary-foreground/50 hover:text-primary-foreground"
              >
                <Eraser className="h-4 w-4" />
              </Button> */}
      </div>

      <div className="px-4">
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("recharges.fields.facility")}</TableHead>
                <TableHead>{t("recharges.fields.fuel_type")}</TableHead>
                <TableHead>{t("recharges.fields.quantity")}</TableHead>
                <TableHead>{t("recharges.fields.date")}</TableHead>
                <TableHead>{t("recharges.fields.invoice")}</TableHead>
                <TableHead>{t("recharges.fields.invoice_amount")}</TableHead>
                <TableHead>{t("recharges.fields.document")}</TableHead>
                <TableHead>{t("recharges.fields.co2_eq")}</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={8}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : data?.results.map((item: EmissionListType, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{item.facility}</TableCell>
                      <TableCell className="text-center">
                        {item.fuel_type}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.quantity} Kg.{" "}
                      </TableCell>
                      <TableCell className="text-center">
                        {format(new Date(item.date), "dd/MM/yyyy", { locale })}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.invoice || "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.invoice_amount || "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center">
                          {item.document_url ? (
                            <FileSpreadsheet
                              size={30}
                              color="#fbfbfb"
                              stroke="#000000"
                              strokeWidth={1}
                            />
                          ) : (
                            "-"
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-black font-bold">
                          {item.co2_eq || "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div
                          role="button"
                          className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                          onClick={() => handleEdit(item.id)}
                        >
                          <Pencil
                            size={30}
                            fill="#000000"
                            stroke="#ffffff"
                            strokeWidth={1}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>

        {data && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(data.count / PAGE_SIZE)}
            onPageChange={handlePageChange}
            hasNextPage={!!data.next}
            hasPreviousPage={!!data.previous}
          />
        )}
      </div>

      <Drawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full max-h-screen w-3/4 min-w-4xl">
          <DrawerHeader>
            <DrawerTitle>
              {selectedEmissionId ? t("recharges.edit") : t("recharges.add")}
            </DrawerTitle>
            <DrawerDescription>
              {selectedEmissionId
                ? t("recharges.edit-description")
                : t("recharges.add-description")}
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto">
            <RechargesForm
              emissionId={selectedEmissionId}
              onSuccess={() => setIsDrawerOpen(false)}
              onCancel={() => setIsDrawerOpen(false)}
            />
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
